import React, { useState, useEffect } from "react";
import { VIMOND_IMAGE_SERVICE_BASE_URL } from "../Api/constants";
import { CurationItem } from "../Api/types";
import { fetchAssetMetadata } from "../Api/services/media/assetMetadata";
import { createLogger } from "../../utils/logger";

const logger = createLogger("ContentTypeIcon");
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const EMPTY_VIEWBOX_D = "M0 0h24v24H0V0z";

interface IconDefinition {
    label: string;
    paths: { d: string; fill?: string }[];
}

const ICON_DEFINITIONS: Record<string, IconDefinition> = {
    list: {
        label: "list icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
            },
        ],
    },
    list_smart: {
        label: "smart list icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z",
            },
        ],
    },
    carousel: {
        label: "carousel icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            { d: "M2 6h4v11H2V6zm5 13h10V4H7v15zM9 6h6v11H9V6zm9 0h4v11h-4V6z" },
        ],
    },
    menu: {
        label: "menu icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            { d: "M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" },
        ],
    },
    page: {
        label: "page icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2H6zm7 1.5L18.5 9H13V3.5zM6 20V4h6v6h6v10H6z",
            },
        ],
    },
    menu_item: {
        label: "menu item icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z",
            },
        ],
    },
    asset: {
        label: "asset icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
            },
        ],
    },
    default: {
        label: "default icon",
        paths: [
            { d: EMPTY_VIEWBOX_D, fill: "none" },
            {
                d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v2h-2V7zm0 4h2v6h-2v-6z",
            },
        ],
    },
};

const renderIcon = (definition: IconDefinition) => (
    <div className="content-type-icon" role="img" aria-label={definition.label}>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height="48px"
            viewBox="0 0 24 24"
            width="48px"
            fill="currentColor"
        >
            {definition.paths.map((pathDef) => (
                <path
                    key={`${definition.label}-${pathDef.d}-${pathDef.fill ?? "none"}`}
                    d={pathDef.d}
                    fill={pathDef.fill}
                />
            ))}
        </svg>
    </div>
);

/**
 * Props for ContentTypeIcon component
 * @typedef {Object} ContentTypeIconProps
 * @property {string | undefined} contentType - The type of content (e.g., "list", "asset", etc.)
 * @property {CurationItem | undefined} item - The item associated with the content (optional)
 */
interface ContentTypeIconProps {
    contentType: string | undefined;
    item?: CurationItem;
}

/**
 * Component to display a content type icon based on the provided content type and item
 * @param contentType
 * @param item
 * @constructor
 * @returns
 */
const ContentTypeIcon: React.FC<ContentTypeIconProps> = ({ contentType, item }) => {
    const [imagePackId, setImagePackId] = useState<string | null>(null);
    const [loadingAssetMetadata, setLoadingAssetMetadata] = useState(false);

    useEffect(() => {
        if (contentType === "asset" && item) {
            if (item.imagePackId) {
                setImagePackId(item.imagePackId);
                return undefined;
            }
            const assetId = item.assetId ?? item.id?.toString();
            if (assetId) {
                let cancelled = false;
                setLoadingAssetMetadata(true);

                fetchAssetMetadata(assetId)
                    .then((metadata) => {
                        if (cancelled) return;
                        if (metadata.imagePackId) {
                            setImagePackId(metadata.imagePackId);
                        } else {
                            logger.warn("No imagePackId found in metadata for asset:", assetId);
                            setImagePackId(null);
                        }
                        setLoadingAssetMetadata(false);
                    })
                    .catch((error) => {
                        if (cancelled) return;
                        logger.warn("Failed to fetch asset metadata:", error);
                        setImagePackId(null);
                        setLoadingAssetMetadata(false);
                    });

                return () => {
                    cancelled = true;
                };
            }
        }
        return undefined;
    }, [contentType, item]);

    if (contentType === "asset" && item) {
        // Show placeholder while loading
        if (!imagePackId && loadingAssetMetadata) {
            return (
                <div
                    className="content-type-icon asset-image"
                    role="img"
                    aria-label="loading asset image"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#3c3c3c",
                    }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 0 24 24"
                        width="24px"
                        fill="currentColor"
                        style={{ opacity: 0.5 }}
                    >
                        <path d="M0 0h24v24H0V0z" fill="none" />
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                </div>
            );
        }

        if (imagePackId) {
            const imageUrl = `${VIMOND_IMAGE_SERVICE_BASE_URL}/api/v2/img/${encodeURIComponent(imagePackId)}`;

            return (
                <div className="content-type-icon asset-image" role="img" aria-label="asset image">
                    <img
                        src={imageUrl}
                        alt={item.title || "Asset"}
                        loading="lazy"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "4px",
                        }}
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                                // Use createElement + appendChild instead of innerHTML for DOM manipulation
                                const svg = document.createElementNS(SVG_NAMESPACE, "svg");
                                svg.setAttribute("xmlns", SVG_NAMESPACE);
                                svg.setAttribute("height", "48px");
                                svg.setAttribute("viewBox", "0 0 24 24");
                                svg.setAttribute("width", "48px");
                                svg.setAttribute("fill", "currentColor");

                                const path1 = document.createElementNS(SVG_NAMESPACE, "path");
                                path1.setAttribute("d", "M0 0h24v24H0V0z");
                                path1.setAttribute("fill", "none");

                                const path2 = document.createElementNS(SVG_NAMESPACE, "path");
                                path2.setAttribute(
                                    "d",
                                    "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
                                );

                                svg.appendChild(path1);
                                svg.appendChild(path2);
                                parent.appendChild(svg);
                            }
                        }}
                    />
                </div>
            );
        }

        // Fallback icon if no imagePackId available
        return (
            <div className="content-type-icon" role="img" aria-label="asset icon">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="48px"
                    viewBox="0 0 24 24"
                    width="48px"
                    fill="currentColor"
                >
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                </svg>
            </div>
        );
    }

    const iconDefinition = ICON_DEFINITIONS[contentType ?? "default"] ?? ICON_DEFINITIONS.default;
    return renderIcon(iconDefinition as IconDefinition);
};

export default ContentTypeIcon;
